import { useCallback, useEffect, useRef, useState } from 'react'
import { contentRegion, focusFirstAlive } from '../a11y/focusFirstAlive'

/**
 * The toast stack's focus and pause bookkeeping, in one place.
 *
 * **Extracted because this is the state that kept being wrong**, repeatedly and
 * in ways no unit assertion could see: focus landing on `<body>`, a hold that
 * never released, two inputs cancelling each other. The lesson worth carrying
 * is in how the effect below is written — it repairs BOTH halves of the pause
 * by one mechanism, rather than special-casing whichever exit path was reported
 * last. Every earlier version special-cased, and every one of them missed the
 * next path.
 *
 * Its tests run through `ToastProvider`, deliberately: the properties are about
 * a mounted stack, and a harness that drove this hook alone would assert the
 * bookkeeping rather than the behaviour. They live in
 * `src/platform/primitives/Toast.test.tsx`.
 *
 * **`paused` is DERIVED from two independent facts, and that is the fix for the
 * fourth.** One boolean with two writers meant either input could clear the
 * other's hold — web-architect measured both directions: a pointer entering and
 * leaving retired the toast holding keyboard focus, and tabbing in and out
 * retired the toast under the cursor. Neither is exotic; a toast that appears
 * under a stationary pointer fires `pointerover`, and any nudge afterwards fires
 * `pointerleave`. Both existing tests were green through it because each drove
 * one input with the other parked outside the host.
 *
 * **`focusWithin`'s half of the pause is now load-bearing for a FOCUS
 * INDICATOR, in another file — issue 216.** `.toaster` suppresses its own ring
 * (`toaster.css`), because it is transparent and a ring on it measured
 * 1.57–1.65:1 over a modal scrim; the visible indicator is on `.toast`, which
 * has an opaque fill. That is safe only while a focused queue is guaranteed to
 * contain a toast — and this pause is the guarantee. With focus in the stack
 * `paused` is true, so `Toast`'s timer effect never sets a timeout and nothing
 * can expire underneath it. web-tester measured it rather than assuming:
 * 5.5s past a 5000ms toast's normal life, still there, still focused.
 *
 * Narrowing this to hover only, or to a bounded extension, would let a focused
 * queue empty — leaving a suppressed indicator on a focused element and
 * nothing at all showing where focus is. Nothing in `toaster.css` can enforce
 * that from where it sits, which is why it is written here as well.
 */
export function useStackFocus(host: HTMLElement | null, queue: readonly unknown[]) {
  const [hovered, setHovered] = useState(false)
  const [focusWithin, setFocusWithin] = useState(false)

  /** Where focus came from, for when a dismissed toast has no neighbour. */
  const restoreTo = useRef<HTMLElement | null>(null)
  /** The control inside the stack that currently holds focus, if any. */
  const focused = useRef<HTMLElement | null>(null)
  /** The toast the pointer is currently over, if any — the hover mirror of it. */
  const hoveredBox = useRef<HTMLElement | null>(null)

  /**
   * The net under every removal the dismiss path does not own.
   *
   * The cap evicts on `show`, which unmounted a focused control with nothing
   * moving focus first — `document.activeElement` measured as `<body>` in
   * Chrome. It is reachable BECAUSE the rest works: focus inside the stack holds
   * the pause, so nothing auto-retires, so arrivals accumulate to the cap and
   * eviction becomes the only thing that can pull a toast out from under a
   * keyboard user.
   *
   * Guarding `show` symmetrically would mean a second copy of the eviction
   * POLICY, not merely of the queue — `show` would have to know which toast
   * `admit()` is about to drop. Watching the committed result covers eviction,
   * and covers whatever removes a toast next without needing to know about it.
   *
   * It reads `focused`, not `document.activeElement`, because the two
   * environments disagree and neither answer is usable: jsdom leaves
   * `activeElement` on the DETACHED node, Chrome moves it to `<body>`. Where
   * focus WAS is the same in both.
   */
  useEffect(() => {
    if (host === null) return

    /**
     * The HOVER half, and it is the same defect one input along.
     *
     * `onPointerLeave` sits on `.toaster`, but the element the pointer is actually over is the `.toast`
     * child — the container is `pointer-events: none`. When that child is
     * removed, React's enter/leave emulation has no surviving `from` fiber, so
     * no leave is dispatched. Exactly the argument this file already made for
     * blur, applied to the other input.
     *
     * It reaches three of the exit paths, and the most ordinary one of all is
     * among them: hovering a toast and clicking its X. Left unrepaired,
     * `hovered` stays true for the life of the provider, nothing auto-retires
     * again, and every arrival leaves by eviction — announced to nobody,
     * because `aria-relevant` is deliberately at its default.
     *
     * Mirroring `focused` rather than querying `:hover` is what makes it
     * testable: jsdom reports `:hover` as false unconditionally, so a
     * DOM-queried repair would clear a hold a test had just established.
     */
    const over = hoveredBox.current
    if (over !== null && !host.contains(over)) {
      hoveredBox.current = null
      setHovered(false)
    }

    const held = focused.current
    if (held === null || host.contains(held)) return
    focused.current = null
    // The first live control rather than the NEXT one, and the difference from
    // the dismiss path is inherent: the box this focus came from is already
    // gone, so there is no position left to walk from.
    focusFirstAlive(
      host.querySelector<HTMLElement>('[data-toast-dismiss]'),
      restoreTo.current,
      contentRegion(),
    )

    /**
     * And reconcile the focus half of the pause with where focus ended up.
     *
     * **No blur is delivered when the focused node is removed** — React drops
     * the event because the fiber is already deleted. Left to the handlers, the
     * hold stuck true and the queue never auto-retired again for the rest of the
     * session: it fills to the cap and holds the foot of the viewport with
     * manual dismissal the only exit.
     *
     * The branch that reaches it is the one where every rung above fails — no
     * neighbour, a trigger that unmounted itself, and no focusable content
     * region. `Toast.test.tsx`'s 'the stack recovers its clock when focus lands
     * nowhere at all' drives exactly that, and it has to kill all three rungs
     * at once: with any one of them alive, the pre-move succeeds, the control
     * is blurred while still connected, and React delivers the event after all.
     *
     * The hover half is repaired above, by the same mechanism and for the same
     * reason.
     */
    const active = document.activeElement
    setFocusWithin(active instanceof Node && host.contains(active))
  }, [host, queue])

  const handlers = {
    // `onPointerOver`, which BUBBLES, rather than `onPointerEnter`, which does
    // not: the pointer moving from one toast to the next must re-record which
    // box it is over, or the ref points at a toast that has since left.
    onPointerOver: (event: React.PointerEvent<HTMLElement>) => {
      // DERIVED from the box, not set alongside it. Two representations of one
      // fact can disagree, and when they did the repair above was dead code:
      // it is guarded on a non-null box, so a pointer over the container but
      // outside any toast set `hovered` with nothing able to clear it.
      // Unreachable in a browser only because
      // `toaster.css` makes the container `pointer-events: none`, which is a
      // load-bearing dependency between two files with nothing tying them
      // together. Deriving removes it.
      const box = event.target instanceof Element ? event.target.closest('[data-toast]') : null
      hoveredBox.current = box as HTMLElement | null
      setHovered(box !== null)
    },
    onPointerLeave: () => {
      setHovered(false)
      hoveredBox.current = null
    },
    onFocus: (event: React.FocusEvent<HTMLElement>) => {
      setFocusWithin(true)
      if (event.target instanceof HTMLElement) focused.current = event.target
      const from = event.relatedTarget
      // Only when focus arrives from OUTSIDE. Moving between the action and the
      // dismiss must not overwrite where the user came from.
      //
      // And never `<body>`: after an eviction Chrome leaves `activeElement`
      // there, so the net's own programmatic refocus arrives with `<body>` as
      // its `relatedTarget` and would overwrite the real trigger with it. The
      // last dismiss then lands on `<main>` instead of the control the user
      // tabbed in from. jsdom cannot see this — it passes a null
      // `relatedTarget` on `.focus()`.
      if (from instanceof HTMLElement && from !== document.body && !event.currentTarget.contains(from)) {
        restoreTo.current = from
      }
    },
    onBlur: (event: React.FocusEvent<HTMLElement>) => {
      // `focusout` bubbles, so this fires when focus moves between two toasts as
      // well as when it leaves. Only the second is a release.
      if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) {
        return
      }
      setFocusWithin(false)
      // **A belt, and the braces are the net above.** The premise this was
      // written on is wrong in Chrome 151: a removal-driven `focusout` fires
      // with `relatedTarget === null` AND `target.isConnected === true`, because
      // Chrome dispatches before it unlinks — so this cannot tell a removal from
      // a tab-away, and it never runs on the removal path anyway because React
      // drops the event. It stays because it costs nothing and because "React
      // does not deliver it" is a fact about a version rather than a contract.
      if (event.target instanceof Node && document.contains(event.target)) {
        restoreTo.current = null
        focused.current = null
      }
    },
  }

  /**
   * Where focus goes when the control holding it is ABOUT to be unmounted.
   *
   * Called before the dispatch, so every candidate is still in the document —
   * which is what lets it walk to the NEXT toast. Someone dismissing a stack is
   * working through it in order, and being sent backwards, or back to the page,
   * between each one is its own defect.
   *
   * The net still lets focus touch `<body>` for a frame, which is long enough
   * for a screen reader to lose its place. The deliberate path should never
   * reach it.
   */
  const focusOnDismiss = useCallback(
    (id: string, notFromPointer: boolean) => {
      if (host === null) return
      const active = document.activeElement
      if (!(active instanceof Node)) return
      const boxes = [...host.querySelectorAll<HTMLElement>('[data-toast]')]
      const index = boxes.findIndex((box) => box.dataset.toast === id)
      // Only when focus is inside the toast ACTUALLY LEAVING — not merely
      // somewhere in the stack. Chrome hides the difference because clicking a
      // button focuses it, so the two coincide; Safari does not focus buttons
      // on click, so with focus on toast A and a click on toast B's X, the
      // looser guard yanked focus off A. An auto-dismiss, or one driven from
      // elsewhere on the page, must not move focus at all.
      if (!boxes[index]?.contains(active)) return

      /**
       * **The neighbour is a KEYBOARD affordance, and offering it to a mouse
       * click paused the whole stack indefinitely.**
       *
       * Every link in that chain was individually right. Chrome and Firefox
       * focus a `<button>` on mousedown, so at the moment of a click focus is
       * inside the leaving toast and the guard above passes. The walk then put
       * focus on the neighbour's dismiss, `onFocus` set `focusWithin`, and
       * `paused` became true with nothing to release it — for a user holding a
       * mouse, with no focus ring, watching two toasts that never go away.
       * Only a click elsewhere on the page released it.
       *
       * So the walk is offered only to the user it is for. A pointer dismissal
       * skips straight out of the host, which still keeps focus off `<body>`
       * and lets the stack resume its clock. Note the flag is "not from a
       * pointer" rather than "from a keyboard": AT-synthesised activations
       * report the same way and should get the walk too — see `Toast`'s prop.
       *
       * Safari is the accidental control: it does not focus buttons on click,
       * so it was already correct where Chrome and Firefox were not.
       */
      focusFirstAlive(
        notFromPointer
          ? boxes[index + 1]?.querySelector<HTMLElement>('[data-toast-dismiss]') ??
              boxes[index - 1]?.querySelector<HTMLElement>('[data-toast-dismiss]')
          : null,
        restoreTo.current,
        contentRegion(),
      )
    },
    // `restoreTo` and `focused` are refs, and `host` is created once. So this
    // identity is stable for the life of the provider — which it MUST be:
    // `dismiss` is memoised on it, `Toast`'s timer effect depends on `dismiss`,
    // and as a plain function declaration this was a fresh object every render.
    // The memoisation was real and the stability it rested on was not, so every
    // `show` and every `dismiss` restarted every live toast's full clock.
    // web-architect proved it A/B: a toast aged 4000ms was still on screen at
    // 5200ms against a 5000ms duration once a second toast arrived. There is no
    // ESLint config in this repo, so `react-hooks/exhaustive-deps` was never
    // going to catch it.
    [host],
  )

  return { paused: hovered || focusWithin, handlers, focusOnDismiss }
}
